import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

const COLORS = {
  primary: '#6366f1',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  dark: '#1e293b',
};

const fmtNum = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

const fmtPct = (num, den) => {
  if (!den || den === 0) return '0.0';
  return ((num / den) * 100).toFixed(1);
};

const thStyle = {
  padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0',
  fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '8px 10px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '12px',
};

const TAB_COLORS = {
  boxed: '#fef9c3',
  tacked: '#d1fae5',
  welded: '#dbeafe',
  shipped: '#e0e7ff',
  readyToShip: '#fef3c7',
  remain: '#fce7f3',
  total: '#f0fdf4',
};

const ShopAnalyticsTabs = ({ projectId, activeTab: controlledTab, hideTabBar = false, asOf = null }) => {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalTab, setInternalTab] = useState('shop1');
  const activeTab = controlledTab != null ? controlledTab : internalTab;
  const [ftPerLoad, setFtPerLoad] = useState(40);
  // Multi-select priority slicer. Tokens are priority-id strings and/or 'blank'.
  // Empty = no filter (all). Options are data-driven (only priorities present).
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [priorityOpen, setPriorityOpen] = useState(false);
  // Hold the available-priority options separately so they survive a filtered
  // fetch that returns zero rows (backend computes them unfiltered, but we keep
  // a local copy in case a later response omits them).
  const [priorityOptions, setPriorityOptions] = useState({ list: [], blankCount: 0 });

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const qs = new URLSearchParams();
      if (asOf) qs.set('asOf', asOf);
      if (selectedPriorities.length > 0) qs.set('priority', selectedPriorities.join(','));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/matrix/project/${projectId}/shop-analytics-tabs${suffix}`
      );
      if (response.data.success) {
        setData(response.data.data);
        const d = response.data.data || {};
        if (Array.isArray(d.availablePriorities)) {
          setPriorityOptions({ list: d.availablePriorities, blankCount: d.blankCount || 0 });
        }
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching shop analytics tabs:', err);
      setError('Failed to load shop analytics');
    } finally {
      setLoading(false);
    }
  }, [projectId, asOf, selectedPriorities, instance, accounts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const togglePriority = useCallback((token) => {
    setSelectedPriorities(prev => prev.includes(token) ? prev.filter(t => t !== token) : [...prev, token]);
  }, []);

  const loadData = useMemo(() => {
    if (!data || !data.totals || ftPerLoad <= 0) return null;
    const { totals } = data;
    const totalLoads = Math.ceil(totals.total_ft / ftPerLoad);
    const shippedLoads = Math.ceil(totals.shipped_ft / ftPerLoad);
    const remainingLoads = Math.max(0, totalLoads - shippedLoads);

    const perShop = data.rows.map(r => ({
      shop: r.shop,
      totalLoads: Math.ceil(r.total_ft / ftPerLoad),
      shippedLoads: Math.ceil(r.shipped_ft / ftPerLoad),
      remainingLoads: Math.max(0, Math.ceil(r.total_ft / ftPerLoad) - Math.ceil(r.shipped_ft / ftPerLoad)),
    }));

    return { totalLoads, shippedLoads, remainingLoads, perShop };
  }, [data, ftPerLoad]);

  const { rows = [], totals = {} } = data || {};

  const renderShop1 = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            <th rowSpan="2" style={{ ...thStyle, textAlign: 'left', borderRight: '2px solid #e2e8f0' }}>Shop</th>
            <th colSpan="5" style={{ ...thStyle, backgroundColor: '#fef9c3', borderBottom: '1px solid #e2e8f0' }}>Count</th>
            <th colSpan="4" style={{ ...thStyle, backgroundColor: '#dbeafe', borderBottom: '1px solid #e2e8f0' }}>ft</th>
            <th colSpan="4" style={{ ...thStyle, backgroundColor: '#d1fae5', borderBottom: '1px solid #e2e8f0' }}>%</th>
            <th colSpan="4" style={{ ...thStyle, backgroundColor: '#fce7f3', borderBottom: '1px solid #e2e8f0' }}>rem ft</th>
          </tr>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            <th style={{ ...thStyle, fontSize: '10px' }}>ISOs</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.boxed }}>boxed</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.tacked }}>tacked</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.welded }}>welded</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.shipped }}>shipped</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.boxed }}>boxed</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.tacked }}>tacked</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.welded }}>welded</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.shipped }}>shipped</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.boxed }}>boxed</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.tacked }}>tacked</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.welded }}>welded</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.shipped }}>shipped</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.boxed }}>boxed</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.tacked }}>tacked</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.welded }}>welded</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: TAB_COLORS.shipped }}>shipped</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={r.shop} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
              <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600', color: r.shop === 'Not Assigned' ? '#94a3b8' : COLORS.dark, borderRight: '2px solid #f3f4f6' }}>{r.shop}</td>
              <td style={tdStyle}>{r.total_isos}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.boxed }}>{r.boxed_count}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.tacked }}>{r.tacked_count}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.welded }}>{r.welded_count}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.shipped }}>{r.shipped_count}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.boxed }}>{fmtNum(r.boxed_ft)}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.tacked }}>{fmtNum(r.tacked_ft)}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.welded }}>{fmtNum(r.welded_ft)}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.shipped }}>{fmtNum(r.shipped_ft)}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.boxed }}>{fmtPct(r.boxed_count, r.total_isos)}%</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.tacked }}>{fmtPct(r.tacked_count, r.total_isos)}%</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.welded }}>{fmtPct(r.welded_count, r.total_isos)}%</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.shipped }}>{fmtPct(r.shipped_count, r.total_isos)}%</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.boxed }}>{fmtNum(r.total_ft - r.boxed_ft)}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.tacked }}>{fmtNum(r.total_ft - r.tacked_ft)}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.welded }}>{fmtNum(r.total_ft - r.welded_ft)}</td>
              <td style={{ ...tdStyle, backgroundColor: TAB_COLORS.shipped }}>{fmtNum(r.total_ft - r.shipped_ft)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '12px' }}>
            <td style={{ padding: '10px', textAlign: 'left' }}>Total</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.total_isos}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.boxed_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.tacked_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.welded_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.shipped_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.boxed_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.tacked_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.welded_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.shipped_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.boxed_count, totals.total_isos)}%</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.tacked_count, totals.total_isos)}%</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.welded_count, totals.total_isos)}%</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.shipped_count, totals.total_isos)}%</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.total_ft - totals.boxed_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.total_ft - totals.tacked_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.total_ft - totals.welded_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.total_ft - totals.shipped_ft)}</td>
          </tr>
        </tfoot>
      </table>
      <div style={{ marginTop: '0.5rem', fontSize: '11px', color: '#94a3b8' }}>
        Thresholds: boxed = 35% shop completion, tacked = 65%, welded = 95%, shipped = load assigned
      </div>
    </div>
  );

  const renderShop2 = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            <th rowSpan="2" style={{ ...thStyle, textAlign: 'left', borderRight: '2px solid #e2e8f0' }}>Shop</th>
            <th colSpan="4" style={{ ...thStyle, backgroundColor: '#fef9c3', borderBottom: '1px solid #e2e8f0' }}>Count</th>
            <th colSpan="4" style={{ ...thStyle, backgroundColor: '#dbeafe', borderBottom: '1px solid #e2e8f0' }}>ft</th>
            <th colSpan="3" style={{ ...thStyle, backgroundColor: '#d1fae5', borderBottom: '1px solid #e2e8f0' }}>%</th>
          </tr>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            <th style={{ ...thStyle, fontSize: '10px' }}>ISOs</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#bbf7d0' }}>Ready to ship</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#a5f3fc' }}>Shipped</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#fecaca' }}>remaining</th>
            <th style={{ ...thStyle, fontSize: '10px' }}>ISOs</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#bbf7d0' }}>Ready to ship</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#a5f3fc' }}>Shipped</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#fecaca' }}>remaining</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#bbf7d0' }}>Ready to ship</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#a5f3fc' }}>Shipped</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#fecaca' }}>remaining</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const remaining_count = r.total_isos - r.shipped_count - r.ready_to_ship_count;
            const remaining_ft = r.total_ft - r.shipped_ft - r.ready_to_ship_ft;
            return (
              <tr key={r.shop} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600', color: r.shop === 'Not Assigned' ? '#94a3b8' : COLORS.dark, borderRight: '2px solid #f3f4f6' }}>{r.shop}</td>
                <td style={tdStyle}>{r.total_isos}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfdf5' }}>{r.ready_to_ship_count}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfeff' }}>{r.shipped_count}</td>
                <td style={{ ...tdStyle, backgroundColor: '#fef2f2' }}>{remaining_count}</td>
                <td style={tdStyle}>{fmtNum(r.total_ft)}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfdf5' }}>{fmtNum(r.ready_to_ship_ft)}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfeff' }}>{fmtNum(r.shipped_ft)}</td>
                <td style={{ ...tdStyle, backgroundColor: '#fef2f2' }}>{fmtNum(remaining_ft)}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfdf5' }}>{fmtPct(r.ready_to_ship_count, r.total_isos)}%</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfeff' }}>{fmtPct(r.shipped_count, r.total_isos)}%</td>
                <td style={{ ...tdStyle, backgroundColor: '#fef2f2' }}>{fmtPct(remaining_count, r.total_isos)}%</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '12px' }}>
            <td style={{ padding: '10px', textAlign: 'left' }}>Total</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.total_isos}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.ready_to_ship_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.shipped_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.total_isos - totals.shipped_count - totals.ready_to_ship_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.total_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.ready_to_ship_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.shipped_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.total_ft - totals.shipped_ft - totals.ready_to_ship_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.ready_to_ship_count, totals.total_isos)}%</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.shipped_count, totals.total_isos)}%</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.total_isos - totals.shipped_count - totals.ready_to_ship_count, totals.total_isos)}%</td>
          </tr>
        </tfoot>
      </table>
      <div style={{ marginTop: '0.5rem', fontSize: '11px', color: '#94a3b8' }}>
        Ready to ship = shop completion &ge; 95% with no load assigned. Shipped = load assigned.
      </div>
    </div>
  );

  const renderShop3 = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            <th rowSpan="2" style={{ ...thStyle, textAlign: 'left', borderRight: '2px solid #e2e8f0' }}>Shop</th>
            <th colSpan="2" style={{ ...thStyle, backgroundColor: '#f0fdf4', borderBottom: '1px solid #e2e8f0' }}>Total</th>
            <th colSpan="3" style={{ ...thStyle, backgroundColor: '#bbf7d0', borderBottom: '1px solid #e2e8f0' }}>shipped</th>
            <th colSpan="3" style={{ ...thStyle, backgroundColor: '#a5f3fc', borderBottom: '1px solid #e2e8f0' }}>ready to ship</th>
            <th colSpan="3" style={{ ...thStyle, backgroundColor: '#fecaca', borderBottom: '1px solid #e2e8f0' }}>remain</th>
          </tr>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            <th style={{ ...thStyle, fontSize: '10px' }}>ISO</th>
            <th style={{ ...thStyle, fontSize: '10px' }}>ft</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#bbf7d0' }}>Count</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#bbf7d0' }}>ft</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#bbf7d0' }}>%</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#a5f3fc' }}>Count</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#a5f3fc' }}>ft</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#a5f3fc' }}>%</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#fecaca' }}>Count</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#fecaca' }}>ft</th>
            <th style={{ ...thStyle, fontSize: '10px', backgroundColor: '#fecaca' }}>%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const remain_count = r.total_isos - r.shipped_count - r.ready_to_ship_count;
            const remain_ft = r.total_ft - r.shipped_ft - r.ready_to_ship_ft;
            return (
              <tr key={r.shop} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600', color: r.shop === 'Not Assigned' ? '#94a3b8' : COLORS.dark, borderRight: '2px solid #f3f4f6' }}>{r.shop}</td>
                <td style={tdStyle}>{r.total_isos}</td>
                <td style={tdStyle}>{fmtNum(r.total_ft)}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfdf5' }}>{r.shipped_count}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfdf5' }}>{fmtNum(r.shipped_ft)}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfdf5', fontWeight: '600', color: COLORS.success }}>{fmtPct(r.shipped_ft, r.total_ft)}%</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfeff' }}>{r.ready_to_ship_count}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfeff' }}>{fmtNum(r.ready_to_ship_ft)}</td>
                <td style={{ ...tdStyle, backgroundColor: '#ecfeff', fontWeight: '600', color: COLORS.info }}>{fmtPct(r.ready_to_ship_ft, r.total_ft)}%</td>
                <td style={{ ...tdStyle, backgroundColor: '#fef2f2' }}>{remain_count}</td>
                <td style={{ ...tdStyle, backgroundColor: '#fef2f2' }}>{fmtNum(remain_ft)}</td>
                <td style={{ ...tdStyle, backgroundColor: '#fef2f2', fontWeight: '600', color: COLORS.danger }}>{fmtPct(remain_ft, r.total_ft)}%</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '12px' }}>
            <td style={{ padding: '10px', textAlign: 'left' }}>Total</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.total_isos}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.total_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.shipped_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.shipped_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.shipped_ft, totals.total_ft)}%</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.ready_to_ship_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.ready_to_ship_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.ready_to_ship_ft, totals.total_ft)}%</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{totals.total_isos - totals.shipped_count - totals.ready_to_ship_count}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtNum(totals.total_ft - totals.shipped_ft - totals.ready_to_ship_ft)}</td>
            <td style={{ padding: '8px', textAlign: 'center' }}>{fmtPct(totals.total_ft - totals.shipped_ft - totals.ready_to_ship_ft, totals.total_ft)}%</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );

  const renderLoad = () => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
        <label style={{ fontSize: '13px', fontWeight: '600', color: COLORS.dark, whiteSpace: 'nowrap' }}>
          Assumption: ft / load
        </label>
        <input
          type="number"
          value={ftPerLoad}
          onChange={(e) => setFtPerLoad(Math.max(1, parseInt(e.target.value) || 1))}
          style={{
            width: 100, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6,
            fontSize: '14px', fontWeight: '600', textAlign: 'center',
          }}
          min="1"
        />
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          Based on this, the load count will be calculated
        </span>
      </div>

      {loadData && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.25rem' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.primary}`, textAlign: 'center' }}>
              <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500', marginBottom: '0.25rem' }}>Total Loads</div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: COLORS.dark }}>{loadData.totalLoads}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{fmtNum(totals.total_ft)} ft total</div>
            </div>
            <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.success}`, textAlign: 'center' }}>
              <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500', marginBottom: '0.25rem' }}>Shipped</div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: COLORS.success }}>{loadData.shippedLoads}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{fmtNum(totals.shipped_ft)} ft shipped</div>
            </div>
            <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.warning}`, textAlign: 'center' }}>
              <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500', marginBottom: '0.25rem' }}>Remaining</div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: COLORS.warning }}>{loadData.remainingLoads}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{fmtNum(totals.total_ft - totals.shipped_ft)} ft remaining</div>
            </div>
          </div>

          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Shop</th>
                  <th style={thStyle}>Total Loads</th>
                  <th style={{ ...thStyle, backgroundColor: '#ecfdf5' }}>Shipped</th>
                  <th style={{ ...thStyle, backgroundColor: '#fef2f2' }}>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {loadData.perShop.map((r, idx) => (
                  <tr key={r.shop} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600', color: r.shop === 'Not Assigned' ? '#94a3b8' : COLORS.dark }}>{r.shop}</td>
                    <td style={tdStyle}>{r.totalLoads}</td>
                    <td style={{ ...tdStyle, backgroundColor: '#ecfdf5', fontWeight: '500', color: COLORS.success }}>{r.shippedLoads}</td>
                    <td style={{ ...tdStyle, backgroundColor: '#fef2f2', fontWeight: '500', color: COLORS.danger }}>{r.remainingLoads}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#1e293b', color: 'white', fontWeight: '700', fontSize: '12px' }}>
                  <td style={{ padding: '10px', textAlign: 'left' }}>Total</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{loadData.totalLoads}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{loadData.shippedLoads}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{loadData.remainingLoads}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );

  const tabs = [
    { id: 'shop1', label: 'Shop 1', desc: 'Boxed/Tacked/Welded/Shipped' },
    { id: 'shop2', label: 'Shop 2', desc: 'Ready/Shipped/Remaining' },
    { id: 'shop3', label: 'Shop 3', desc: 'Summary by Status' },
    { id: 'load', label: 'Load', desc: 'Load Calculator' },
  ];

  return (
    <div style={{ padding: '0.5rem' }}>
      {!hideTabBar && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setInternalTab(tab.id)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: activeTab === tab.id ? COLORS.primary : 'transparent',
                color: activeTab === tab.id ? 'white' : '#64748b',
                border: activeTab === tab.id ? 'none' : '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600',
                transition: 'all 0.2s',
              }}
              title={tab.desc}
            >
              {tab.label}
            </button>
          ))}
          <button
            onClick={fetchData}
            style={{
              marginLeft: 'auto', padding: '0.5rem 1rem', backgroundColor: '#f1f5f9',
              border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer',
              fontSize: '0.8rem', color: '#475569', fontWeight: '500',
            }}
          >
            Refresh
          </button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>Priority</label>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setPriorityOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem',
              border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.82rem', color: '#1e293b',
              backgroundColor: '#fff', cursor: 'pointer', minWidth: 180, justifyContent: 'space-between',
            }}
          >
            <span>
              {selectedPriorities.length === 0
                ? 'All priorities'
                : `${selectedPriorities.length} selected`}
            </span>
            <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{priorityOpen ? '\u25B2' : '\u25BC'}</span>
          </button>

          {priorityOpen && (
            <>
              <div
                onClick={() => setPriorityOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 41, minWidth: 220, maxHeight: 280,
                overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '0.35rem',
              }}>
                {priorityOptions.list.length === 0 && priorityOptions.blankCount === 0 ? (
                  <div style={{ padding: '0.5rem', fontSize: '0.78rem', color: '#94a3b8' }}>No priorities in this data.</div>
                ) : (
                  <>
                    {priorityOptions.list.map(p => {
                      const token = String(p.id);
                      const checked = selectedPriorities.includes(token);
                      return (
                        <label key={token} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.5rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', color: '#1e293b' }}>
                          <input type="checkbox" checked={checked} onChange={() => togglePriority(token)} />
                          <span style={{ flex: 1 }}>{p.name}</span>
                          <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{p.count}</span>
                        </label>
                      );
                    })}
                    {priorityOptions.blankCount > 0 && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.5rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', color: '#64748b', borderTop: '1px solid #f1f5f9', marginTop: '0.2rem' }}>
                        <input type="checkbox" checked={selectedPriorities.includes('blank')} onChange={() => togglePriority('blank')} />
                        <span style={{ flex: 1, fontStyle: 'italic' }}>(Blank \u2014 no priority)</span>
                        <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{priorityOptions.blankCount}</span>
                      </label>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
        {selectedPriorities.length > 0 && (
          <button
            onClick={() => setSelectedPriorities([])}
            style={{ padding: '0.35rem 0.6rem', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', color: '#475569', fontWeight: '500' }}
          >
            Clear
          </button>
        )}
      </div>

      <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '1rem', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTop: `3px solid ${COLORS.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <div style={{ color: '#64748b', fontSize: '13px' }}>Loading shop analytics...</div>
            </div>
          </div>
        ) : error ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ color: COLORS.danger, fontWeight: '600', marginBottom: '0.5rem' }}>{error}</div>
            <button onClick={fetchData} style={{ padding: '8px 20px', backgroundColor: COLORS.primary, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: '600' }}>Retry</button>
          </div>
        ) : (!data || !data.rows || data.rows.length === 0) ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
            {selectedPriorities.length > 0 ? 'No shop data for the selected priorities.' : 'No shop data available for this project.'}
          </div>
        ) : (
          <>
            {activeTab === 'shop1' && renderShop1()}
            {activeTab === 'shop2' && renderShop2()}
            {activeTab === 'shop3' && renderShop3()}
            {activeTab === 'load' && renderLoad()}
          </>
        )}
      </div>
    </div>
  );
};

export default ShopAnalyticsTabs;
